import '../../dev';
import './viewer-component';
import * as directionalLightingView from '../../views/directional-lighting-view';
import * as testRooms from './test-rooms';
import CommandHistory from './command-history';
import FirstPersonTool from '../../tools/first-person-tool';
import OrbitRetargetTool from '../../tools/orbit-retarget-tool';
import OrbitTool from '../../tools/new-orbit-tool';
import Overhead3DMovementTool from '../../tools/overhead-3d-movement-tool';
import RenderLoop from '../../control-flow/render-loop';
import RoomView from '../../views/room-view';
import Signal from '../../control-flow/signal';
import UpdateSceneEnvironment from '../../view3d/update-scene-environment';
import ViewerComponent from './viewer-component';
import elt from '../../html/elt';
import exportForRender from '../../view3d/export-for-render';
import logging from '../../logging';
import makeStandardRenderer from '../../view3d/make-standard-renderer';
import mfac from '../../material-factory';
import traverse from '../../view3d/traverse-generator';
import { Camera, DefaultLoadingManager, PerspectiveCamera, Scene, WebGLRenderer } from 'three';
import { CameraConfig } from '../../views/views-config';
import { CommandManager } from '../../managers/command-manager';
import { Material, Room } from '../../domain';
import { QUARTER_TURN } from '../../math/constants';
import { RoulerPromise } from '../../animation/rouler';
import { ViewerConfig, viewerDefaults, ViewerOptions } from './config-options';
import { animatePose } from '../../animation/animate-pose';
import { applySavedViewCamera, extractSavedViewCamera } from '../../controllers/saved-view-controllers';
import { clone, update } from "../../json/optional-properties";
import { defer } from '../../control-flow/defer';
import { easeInOutSine } from '../../animation/easing';
import { findRoomCenter } from '../../controllers/room-controllers';
import { onChange as onAnimatePoseChange } from '../../animation/animate-pose';
import { saveBuffer } from '../../utils/save-buffer';
import { setupResizeHandler } from '../../view3d/setup-resize-handler';

const log = logging('ViewerApp');

// A brisk 5 frame transition (at 30 fps) expressed in milliseconds
const TRANSITION_DURATION = 167;

// TODO: also store the camera position in local storage so that a refresh
// appears as a no-op.


/**
 *
 */
export class ViewerApp {

    component: ViewerComponent;
    loop: RenderLoop;

    navtools: {
        orbit: OrbitTool,
        retarget: OrbitRetargetTool,
        fp: FirstPersonTool,
        overhead: Overhead3DMovementTool,
    };

    transition: {
        promise?: RoulerPromise,
        camera: PerspectiveCamera,
    } = {
        camera: new PerspectiveCamera(),
    }

    navModeViews: {
        orbit: CameraConfig,
        fp: CameraConfig,
        overhead: CameraConfig,
    }

    navMode: keyof typeof this.navModeViews = "orbit";
    
    model: Room;
    view: RoomView;

    config: ViewerConfig = viewerDefaults;

    scene: Scene;
    camera: Camera;
    canvas: HTMLCanvasElement;
    renderer: WebGLRenderer;
    sceneEnvironment: UpdateSceneEnvironment;

    commands = new CommandManager('viewer');
    history = new CommandHistory();

    onExit = new Signal('onExit', 'viewer');
    onStart = new Signal('onStart', 'viewer');

    constructor(options: ViewerOptions = {}) {
        update(this.config, options);
        this.loop = new RenderLoop(this.render);

        onAnimatePoseChange.add(this.loop.update);
    }

    async start() {
        log.debug('ViewerApp.start()');

        this.setupCommands();
        this.component = elt('viewer-component');
        this.component.showExitCommand(!this.config.standalone);
        document.body.append(this.component);
        this.canvas = this.component.canvas;

        if(!this.model) {
            const roomJSON = window.localStorage.getItem('ViewerApp/room');
            if(roomJSON) {
                try {
                    this.model = Room.fromJSON(JSON.parse(roomJSON));
                } catch(error) {
                    log.error(error);
                    log.warn('Stored room invalid, falling back to empty');
                    this.model = new Room();
                }
            } else {
                this.model = Room.fromJSON(testRooms.defaultRoom);
            }
        }

        if(!this.scene) {
            this.scene = new Scene();
        }

        if(!this.camera) {
            this.camera = new PerspectiveCamera(75, 1.0, 0.1, 1000);
            // TODO: need a more sensible default camera position
            this.camera.position.set(24, 67, 96);
            this.camera.lookAt(0, 0, 0);
        }

        this.renderer = makeStandardRenderer(this.canvas);
        this.sceneEnvironment = new UpdateSceneEnvironment(this.renderer, DefaultLoadingManager);

        window.addEventListener('keydown', (event: KeyboardEvent) => {
            switch(event.key) {
                case 'Esc':
                case 'Escape':
                    this.commands.trigger('update');
                    break;

                case 'd':
                    if(event.ctrlKey) {
                        event.preventDefault();
                        console.log('calling reset-room');
                        this.commands.trigger('reset-room');
                    }
                    break;
            }
        });

        // Set up the nav tools
        {
            // Note that the orbit tool's presets can move the camera, so it's
            // best to do this prior to setting up the initial camera positions
            // for the nav modes. Ultimately we'll start with the first person
            // controls, which will not move the camera implicitly.
            const orbit = new OrbitTool(this.camera, this.canvas);
            orbit.applyPreset("free");
            orbit.target.set(0, 0, 0);
            orbit.enable();

            orbit.onChange.add(this.loop.update);

            const retarget = new OrbitRetargetTool(orbit, this.scene);
            retarget.enable();
            retarget.onChange.add(this.loop.update);

            const fp = new FirstPersonTool(this.camera, this.canvas);
            fp.onChange.add(this.loop.update);

            const overhead = new Overhead3DMovementTool(this.camera, this.canvas);
            overhead.onChange.add(this.loop.update);

            this.navtools = { orbit, retarget, fp, overhead };
        }

        // Set up the nav mode views
        this.navModeViews = {} as any;
        this.saveNavModeView();
        this.navModeViews.fp = clone(this.navModeViews.orbit);
        this.navModeViews.overhead = clone(this.navModeViews.orbit);
        // TODO: implement a more sophisticated way of framing the whole
        // room within the overhead camera's frame.
        const [x, z] = findRoomCenter(this.model.walls);
        this.navModeViews.overhead.position = {x, y: 240, z};
        this.navModeViews.overhead.rotation = {x: -QUARTER_TURN, y: 0, z: 0};

        mfac.onLoad.add(this.loop.update);

        setupResizeHandler(this.canvas, this.camera, this.renderer, this.loop.update);

        if(this.config.standalone) {
            await this.refresh();
        }
        this.component.editor.setJSON(this.model);
        this.loop.start();

        // We defer this onStart call to give code a chance to register the
        // handler before this is triggered, in the case where there is no real
        // async delay in starting.
        defer(this.onStart.trigger);
    }

    async refresh() {

        this.view?.removeFromParent();
        this.view = new RoomView(this.model as Room, this.config.roomView);
        this.scene.add(this.view);

        for(const obj of traverse(this.scene)) {
            if(obj instanceof directionalLightingView.DirectionalLightingView) {
                obj.removeFromParent();
            }
        }
        this.loop.onBeforeRender.delete(this.updateLights);

        if(this.config.roomView.tempIncludeGlobalLighting) {
            this.scene.add(directionalLightingView.get(this.model as Room));
            directionalLightingView.optimize(this.model as Room);

            this.sceneEnvironment.update(this.scene, this.config.environmentAssetId);

            this.loop.onBeforeRender.add(this.updateLights);
        }

        this.loop.update();

    }

    updateLights = () => {
        directionalLightingView.update(this.model as Room, this.config.roomView.directionalLighting);
    }

    setupCommands() {
        this.commands.on('update', () => {
            log.debug('Viewer update command');
            const
                json = this.component.editor.getDocumentText(),
                newData = JSON.parse(json);
            window.localStorage.setItem('ViewerApp/room', json);
            this.updateModelWithUndo('update', newData);
        });

        this.commands.on('update-json', () => {
            this.component.editor.setJSON(this.model);
        });

        this.commands.on('load', (which: string) => {
            let data: any;
            if(which in testRooms) {
                data = Room.fromJSON(testRooms[which]);
            } else {
                log.error(`ViewerApp load command, unknown default named "${which}"`)
            }
            window.localStorage.setItem('ViewerApp/room', JSON.stringify(data));
            this.updateModelWithUndo('load', data);
        });

        this.commands.on('reset', () => {
            log.debug('ViewerApp reset command');
            window.localStorage.removeItem('ViewerApp/room');
            this.updateModelWithUndo('reset', testRooms.defaultRoom);
        });

        this.commands.on('undo', () => {
            this.history.undo();
            window.localStorage.setItem('ViewerApp/room', JSON.stringify(this.model));
            this.refresh();
        });

        this.commands.on('redo', () => {
            this.history.redo();
            window.localStorage.setItem('ViewerApp/room', JSON.stringify(this.model));
            this.refresh();
        });

        this.commands.on('repaint', this.loop.update);
        this.commands.on('pipeline-update', this.loop.update);

        this.commands.on('refresh', this.refresh.bind(this));

        this.commands.on('walk', () => {
            this.saveNavModeView();
            this.navMode = 'fp';
            this.restoreNavModeView();

            this.navtools.orbit.disable();
            this.navtools.retarget.disable();
            this.navtools.fp.enable(); // <- enabled
            this.navtools.overhead.disable();
            this.loop.update();
        });

        this.commands.on('orbit', async () => {
            this.saveNavModeView();
            this.navMode = 'orbit';
            await this.restoreNavModeView();

            this.navtools.orbit.enable(); // <- enabled
            this.navtools.retarget.enable(); // <- enabled

            if(this.transition.promise) this.transition.promise.canceled = true;
            this.transition.camera.position.copy(this.camera.position);
            this.transition.camera.quaternion.copy(this.camera.quaternion);
            this.transition.camera.lookAt(this.navtools.orbit.target);
            this.transition.promise = animatePose(
                TRANSITION_DURATION,
                this.camera,
                this.transition.camera,
                (t: number) => {
                    this.loop.update();
                    return easeInOutSine(t);
                });

            this.navtools.fp.disable();
            this.navtools.overhead.disable();

            this.loop.update();
        });

        this.commands.on('overhead', () => {
            this.saveNavModeView();
            this.navMode = 'overhead';
            this.restoreNavModeView();

            this.navtools.orbit.disable();
            this.navtools.retarget.disable();
            this.navtools.fp.disable();
            this.navtools.overhead.enable(); // <-- enabled

            this.loop.update();
        });

        this.commands.on('render.glb', async () => {
            // We clone the scene, which will do a bad job with the view
            // classes, but we'll fix it in a minute.
            const scene = this.scene.clone(true);
            scene.userData.cpsCameraData =
                extractSavedViewCamera(this.camera as PerspectiveCamera);
            for(let obj of traverse(scene)) {
                if(obj instanceof RoomView) {
                    obj.removeFromParent();
                }
            }
            await new Promise<void>(resolve => {
                const model = this.model.clone(true);
                model.commit(true);
                const view = new RoomView(model, this.config.roomView);
                view.onReady.add(resolve);
                scene.add(view);
                // We preload all of the materials to help onReady resolve
                // correctly. TODO: formalize this somewhere.
                model.traverse((model) => {
                    if(model instanceof Material) {
                        mfac.makefrom(model);
                    }
                });
            });
            saveBuffer(await exportForRender(scene), 'render.glb');
        });

        this.commands.on('exit', () => {
            this.onExit.trigger();
            remove(); // removes all the viewer components
        });
    }

    saveNavModeView() {
        this.navModeViews[this.navMode] = extractSavedViewCamera(this.camera as PerspectiveCamera);
    }

    async restoreNavModeView() {
        const savedView = this.navModeViews[this.navMode];
        await applySavedViewCamera(savedView, this.camera); 
    }


    updateModelWithUndo(description: string, data: any) {
            const
                oldData = this.model.toJSON(),
                newData = data;
            this.history.add({
                description,
                undo: () => {
                    this.model = Room.fromJSON(oldData);
                    this.component.editor.setJSON(this.model);
                },
                redo: () => {
                    this.model = Room.fromJSON(newData);
                    this.component.editor.setJSON(this.model);
                }
            });
            this.model = Room.fromJSON(data);
            this.component.editor.setJSON(this.model);
            this.refresh();
    }

    render = () => {
        this.renderer.render(this.scene, this.camera);
    }
}
export default ViewerApp;



/**
 * The overload declaration for the init function. This allows for several use
 * cases where we want to inspect only the domain model, a live scene, or to
 * use it stand-alone for testing.
 */
type ViewerInitFunction = {
    /**
     * Attempt to find a Room, Camera, and/or Scene on the Window object. If
     * multiple exist, the first ones encountered are used.
     */
    (config?: ViewerOptions): ViewerApp;

    /**
     * Constructs a new scene from the given Room instance and uses a default
     * perspective camera.
     */
    (model: Room, config?: ViewerOptions): ViewerApp;

    /**
     * Displays scene using the given camera, scene, and room. Note that
     * updating the room domain model will result in the RoomView being removed
     * and re-added to the scene, but that any objects outside of the RoomView
     * will remain intact, e.g. the main lights.
     */
    (model: Room, camera: Camera, scene: Scene, config?: ViewerOptions): ViewerApp;
}

/**
 * A smart init function that tries to find existing room/camera/scene on the
 * window object, or can be explicitly given. The camera is always cloned to
 * separate it from the camera in use.
 */
export const init: ViewerInitFunction =
    (room?: Room | ViewerOptions, camera?: Camera | ViewerOptions,
     scene?: Scene, options?: ViewerOptions) =>
{
    const a = room, b = camera, c = scene, d = options;
    if(a instanceof Room && b instanceof Camera && c instanceof Scene) {
        return initRoomCameraSceneConfig(a, b, c, d);
    }
    else if(a instanceof Room) {
        return initRoomConfig(a, b as ViewerOptions);
    }
    else if(a && a['standalone']) {
        const viewer = new ViewerApp(a as ViewerConfig);
        viewer.start();
        return viewer;
    }
    else {
        return initConfig(a);
    }
    
}

const initConfig = (config: ViewerOptions = {}) => {
    let room: Room, camera: Camera, scene: Scene;
    for(const item of Object.values(window)) {
        if(room == null && item instanceof Room) {
            room = item;
        }
        if(camera == null && item instanceof Camera) {
            camera = item;
        }
        if(scene == null && item instanceof Scene) {
            scene = item;
        }
    }

    if(room == null || camera == null || scene == null) {
        return initRoomConfig(new Room(), config);
    }
    else {
        return initRoomCameraSceneConfig(room, camera, scene, config);
    }
}

const initRoomConfig = (model: Room, config: ViewerOptions = {}) => {
    const app = new ViewerApp(config);
    app.model = model;
    app.start();
    return app;
}

const initRoomCameraSceneConfig =
    (model: Room, camera: Camera, scene: Scene, config: ViewerOptions = {}) =>
{
    const app = new ViewerApp(config);
    app.model = model;
    app.camera = new PerspectiveCamera();
    app.camera.position.copy(camera.position);
    app.camera.rotation.copy(camera.rotation);
    app.scene = scene;
    app.start();
    return app;
}

/**
 * This would be accessed as cps.viewer.remove() from the console.
 */
export const remove = () => {
    for(const viewer of document.querySelectorAll('viewer-component')) {
        viewer.remove();
    }
};
